export enum LayoutType {
    Landscape_16_9 = 'Landscape_16_9',
    Portrait_9_16 = 'Portrait_9_16'
}

export enum LayoutMainZoneType {
    Media = 'Media',
    Playlist = 'Playlist',
    Schedule = 'Schedule',
    Broadcast = 'Broadcast',
    CustomText = 'CustomText',
    Widget = 'Widget'
}

export class LayoutStylingModel {
    backgroundColor: string = '#0A0A0A';
    primaryColor: string = '#3B82F6';
    fontFamily: string = 'Inter';
    logoUrl: string = '';
}

// ---------------------------------------------------------------------------
// Dynamic zone state — keyed by sectionCode (e.g. 'SEG1', 'SEG2', ...)
// ---------------------------------------------------------------------------

export interface ZoneState {
    type: LayoutMainZoneType;
    targetId: string | null;
    targetName: string;
    customText: string;
    widgets: string[];
    /** Per-instance text for CustomText widgets. Keys: 'CustomText', 'CustomText_1', 'CustomText_2', … */
    widgetTexts: Record<string, string>;
    logoUrl: string;
    /** Hex color for the zone background, e.g. '#1a1a2e'. Empty = transparent/default. */
    backgroundColor: string;
    /** CSS background-image value, e.g. 'url(https://...)'. Empty = none. */
    backgroundImage: string;
    /** JSON string with weather widget config: { city, isCelsius }. */
    weatherProperties: string;
    /** JSON string with crawl widget config: { lines, transition, order, numbered, speed }. */
    textCrawlProperties: string;
    /** Whether to show the blurred fill background behind contained media images. Default true. */
    showMediaFill: boolean;
    /** Per-widget font scale presets. Keys match widget keys ('Weather', 'DateTime', 'CustomText', etc.). */
    widgetSizes: Record<string, 'small' | 'standard' | 'large'>;
}

export type ZoneStateMap = Record<string, ZoneState>;

export function defaultZoneState(): ZoneState {
    return {
        type: LayoutMainZoneType.Playlist, targetId: null, targetName: '',
        customText: '', widgets: [], widgetTexts: {}, logoUrl: '',
        backgroundColor: '#000000', backgroundImage: '', weatherProperties: '', textCrawlProperties: '',
        showMediaFill: true, widgetSizes: {}
    };
}

/** Returns the default zone state for a section based on its 0-based display order index. */
export function defaultZoneStateForIndex(index: number): ZoneState {
    const defaults: LayoutMainZoneType[] = [
        LayoutMainZoneType.Playlist,    // Zone 1
        LayoutMainZoneType.Media,       // Zone 2
        LayoutMainZoneType.Media,       // Zone 3
        LayoutMainZoneType.Widget,       // Zone 4
    ];
    const type = defaults[index] ?? LayoutMainZoneType.Playlist;
    return {
        type, targetId: null, targetName: '',
        customText: '', widgets: [], widgetTexts: {}, logoUrl: '',
        backgroundColor: '#000000', backgroundImage: '', weatherProperties: '', textCrawlProperties: '',
        showMediaFill: true, widgetSizes: {}
    };
}

// ---------------------------------------------------------------------------
// Frontend Add / Edit models
// ---------------------------------------------------------------------------

export class LayoutAddModel {
    name: string = '';
    type: LayoutType = LayoutType.Landscape_16_9;
    showZoneBorders: boolean = true;
    groupId: number = -1;
    folderId: number = null;
    layoutTemplateId: number = null;
    styling: LayoutStylingModel = new LayoutStylingModel();
    zoneStates: ZoneStateMap = {};
    displayIds: Array<number> = [];
    /** Auto-refresh (DN-4173): anchor time-of-day, default 3:00 AM. */
    refreshTime: Date = new Date(new Date().setHours(3, 0, 0, 0));
    /** Auto-refresh cadence in hours (1/3/6/12/24), default once a day. */
    refreshIntervalHours: number = 24;
}

export class LayoutEditModel extends LayoutAddModel {
    id: string = '';
}

// ---------------------------------------------------------------------------
// Backend request models (sent to the API)
// ---------------------------------------------------------------------------

export class LayoutSectionPlaylistModel {
    displayListId: number = null;
    displayOrder: number = 1;
    isScheduled: boolean = false;
    startDateTime: string = null;
    endDateTime: string = null;
    transitionType: string = null;
    durationInSeconds: number = null;
}

export class LayoutSectionWidgetModel {
    code: string = '';
    type: string = '';
    displayOrder: number = 1;
    properties: string = null;
}

export class LayoutSectionModel {
    sectionCode: string = '';
    displayOrder: number = 1;
    isScheduled: boolean = false;
    startDateTime: string = null;
    endDateTime: string = null;
    sectionDisplayLists: LayoutSectionPlaylistModel[] = [];
    sectionBroadcastId: number = null;
    sectionSchedulerId: number = null;
    sectionUserMediaAppId: number = null;
    sectionProperties: string = '{}';
    widgets: LayoutSectionWidgetModel[] = [];
}

export class LayoutApiAddModel {
    name: string = '';
    description: string = null;
    layoutTemplateId: number = null;
    groupId: number = null;
    folderId: number = null;
    transitionType: string = null;
    transitionDuration: number = 500;
    showPromotionalText: boolean = false;
    layoutProperties: string = '{}';
    refreshTime: string = '03:00:00';
    refreshIntervalHours: number = 24;
    isScheduled: boolean = false;
    startDateType: number = 0;
    startDate: string = null;
    startTime: string = null;
    startDateTime: string = null;
    endDateType: number = 0;
    endDate: string = null;
    endTime: string = null;
    endDateTime: string = null;
    timeZone: string = 'UTC';
    isRecurring: boolean = false;
    recurringType: number = null;
    recurringSeparationCount: number = null;
    recurringEndDate: string = null;
    recurringDays: string = null;
    sections: LayoutSectionModel[] = [];
    displayIds: Array<number> = [];
}

export class LayoutApiEditModel extends LayoutApiAddModel {
    id: number = null;
}

// ---------------------------------------------------------------------------
// Backend response DTOs (received from the API)
// ---------------------------------------------------------------------------

export class LayoutTemplateSectionModel {
    id: number;
    layoutTemplateId: number;
    sectionCode: string;
    sectionName: string;
    description: string;
    displayOrder: number;
    allowsDisplayLists: boolean;
    allowsBroadcasts: boolean;
    allowsSchedules: boolean;
    allowsMediaApps: boolean;
    allowedMediaApps: string[] | null = null;
}

export class LayoutTemplateModel {
    id: number;
    name: string;
    description: string;
    filePath: string;
    previewImageUrl: string;
    isActive: boolean;
    displayOrder: number;
    sectionCount: number;
    createdOn: string;
    sections: LayoutTemplateSectionModel[] = [];
}

export class LayoutSectionPlaylistDto {
    displayListId: number;
    name: string;
    description: string;
    transitionType: string;
    mediaCount: number;
    createdOn: string;
    createdBy: string;
    isPaidAddOn?: boolean;
}

export class LayoutSectionBroadcastDto {
    broadcastId: number;
    name: string;
    description: string;
    status: string;
    createdOn: string;
    createdBy: string;
    isPaidAddOn?: boolean;
}

export class LayoutSectionSchedulerDto {
    schedulerId: number;
    name: string;
    description: string;
    timeZone: string;
    eventCount: number;
    createdOn: string;
    createdBy: string;
}

export class LayoutSectionMediaAppDto {
    userMediaAppId: number;
    name: string;
    description: string;
    mediaAppName: string;
    createdOn: string;
    createdBy: string;
    isPaidAddOn?: boolean;
}

export class LayoutSectionWidgetDto {
    id: number;
    code: string;
    type: string;
    displayOrder: number;
    properties: string;
    status: string;
    createdOn: string;
    createdBy: string;
}

export class LayoutSectionDto {
    sectionCode: string;
    sectionName: string;
    description: string;
    displayOrder: number;
    isScheduled: boolean;
    startDateTime: string;
    endDateTime: string;
    allowsDisplayLists: boolean;
    allowsBroadcasts: boolean;
    allowsSchedules: boolean;
    allowsMediaApps: boolean;
    sectionProperties: string;
    sectionDisplayLists: LayoutSectionPlaylistDto[] = [];
    sectionBroadcast: LayoutSectionBroadcastDto = null;
    sectionScheduler: LayoutSectionSchedulerDto = null;
    sectionMediaApp: LayoutSectionMediaAppDto = null;
    widgets: LayoutSectionWidgetDto[] = [];
    userMediaAppForEdit?: any;
    userMediaAppUrlDto?: any;
    displayListsDetailDtos?: any[];
}

export class LayoutForEditDto {
    id: number;
    name: string;
    description: string;
    layoutTemplateId: number;
    displayIds: number[] = [];
    groupId: number;
    folderId: number;
    transitionType: string;
    transitionDuration: number;
    showPromotionalText: boolean;
    layoutProperties: string;
    refreshTime: string;
    refreshIntervalHours: number;
    isScheduled: boolean;
    startDateType: number;
    startDate: string;
    startTime: string;
    startDateTime: string;
    endDateType: number;
    endDate: string;
    endTime: string;
    endDateTime: string;
    timeZone: string;
    isRecurring: boolean;
    recurringType: number;
    recurringSeparationCount: number;
    recurringEndDate: string;
    recurringDays: string;
    sections: LayoutSectionDto[] = [];
}

export class LayoutListItemDto {
    id: number;
    name: string;
    description: string;
    layoutTemplateId: number;
    layoutTemplateName: string;
    sectionCount: number;
    displayCount: number;
    status: string;
    createdOn: string;
    updatedOn: string;
    createdBy: string;
    folderId: number;
    folderName: string;
    groupId: number;
    groupName: string;
}

export class LayoutChangeStatusModel {
    id: number = null;
    status: number = 0;
}

export class LayoutChangeGroupModel {
    layoutId: number = null;
    groupId: number = null;
}

// ---------------------------------------------------------------------------
// Mapping helpers: ZoneStateMap ↔ LayoutSectionModel[]
// ---------------------------------------------------------------------------

/**
 * Converts the dynamic zone-state map into the backend sections[] array.
 * Only zones that have a selected target (or CustomText with content) are included.
 * displayOrder follows the order entries appear in the map (insertion order).
 */
export function mapZoneStatesToSections(zoneStates: ZoneStateMap): LayoutSectionModel[] {
    const sections: LayoutSectionModel[] = [];
    let order = 1;

    Object.entries(zoneStates).forEach(([sectionCode, state]) => {
        const hasCustomText = state.type === LayoutMainZoneType.CustomText &&
            !!state.customText && state.customText !== '<p><br></p>';
        const hasWidgets = state.type === LayoutMainZoneType.Widget &&
            (state.widgets?.length ?? 0) > 0;

        if (!state.targetId && !hasCustomText && !hasWidgets) { return; }

        const section = new LayoutSectionModel();
        section.sectionCode  = sectionCode;
        section.displayOrder = order++;

        // Build the base sectionProperties bag (bg + weather apply to all zone types)
        const sectionProps: Record<string, string> = {};
        if (state.backgroundColor) { sectionProps['backgroundColor'] = state.backgroundColor; }
        if (state.backgroundImage)  { sectionProps['backgroundImage']  = state.backgroundImage; }
        if (state.weatherProperties) { sectionProps['weatherProperties'] = state.weatherProperties; }
        if (state.showMediaFill === false) { sectionProps['showMediaFill'] = 'false'; }

        switch (state.type) {
            case LayoutMainZoneType.Playlist: {
                const playlistIds = (state.targetId || '')
                    .split(',')
                    .map(x => x.trim())
                    .filter(x => !!x)
                    .map(x => parseInt(x, 10))
                    .filter(x => !isNaN(x));

                section.sectionDisplayLists = playlistIds.map((id, index) => {
                    const p = new LayoutSectionPlaylistModel();
                    p.displayListId = id;
                    p.displayOrder  = index + 1;
                    return p;
                });
                break;
            }
            case LayoutMainZoneType.Broadcast:
                section.sectionBroadcastId = parseInt(state.targetId, 10);
                break;
            case LayoutMainZoneType.Schedule:
                section.sectionSchedulerId = parseInt(state.targetId, 10);
                break;
            case LayoutMainZoneType.Media:
                section.sectionUserMediaAppId = parseInt(state.targetId, 10);
                break;
            case LayoutMainZoneType.CustomText:
                sectionProps['customText'] = state.customText;
                break;
            case LayoutMainZoneType.Widget:
                section.widgets = (state.widgets ?? []).map((key, idx) => {
                    const w = new LayoutSectionWidgetModel();
                    // Normalize indexed CustomText keys ('CustomText_1' etc.) to base type for backend
                    const baseType = key.startsWith('CustomText') ? 'CustomText' : key;
                    w.code = baseType.toUpperCase();
                    w.type = baseType;
                    w.displayOrder = idx + 1;
                    if (baseType === 'Logo' && state.logoUrl) {
                        w.properties = JSON.stringify({ logoUrl: state.logoUrl });
                    }
                    if (baseType === 'Weather' && state.weatherProperties) {
                        w.properties = state.weatherProperties;
                    }
                    if (baseType === 'CustomText') {
                        // Use per-instance text from widgetTexts; fall back to legacy customText for first instance
                        const text = state.widgetTexts?.[key] ?? (key === 'CustomText' ? state.customText : '');
                        if (text) { w.properties = JSON.stringify({ customText: text }); }
                    }
                    if (baseType === 'TextCrawl' && state.textCrawlProperties) {
                        w.properties = state.textCrawlProperties;
                    }
                    // Inject per-widget size into properties
                    const widgetSize = state.widgetSizes?.[key];
                    if (widgetSize && widgetSize !== 'standard') {
                        let propsObj: any = {};
                        try { propsObj = w.properties ? JSON.parse(w.properties) : {}; } catch { /* ignore */ }
                        propsObj.size = widgetSize;
                        w.properties = JSON.stringify(propsObj);
                    }
                    return w;
                });
                sectionProps['logoUrl'] = state.logoUrl ?? '';
                break;
        }

        section.sectionProperties = JSON.stringify(sectionProps);

        sections.push(section);
    });

    return sections;
}

/**
 * Converts the backend sections[] array into a ZoneStateMap.
 * Used when loading a layout for editing.
 */
export function mapSectionsToZoneStates(sections: LayoutSectionDto[]): ZoneStateMap {
    const zoneStates: ZoneStateMap = {};

    sections.forEach(section => {
        const code = section.sectionCode;
        if (!code) { return; }

        const state = defaultZoneState();

        if (section.sectionDisplayLists?.length) {
            state.type       = LayoutMainZoneType.Playlist;
            const ids = section.sectionDisplayLists
                .map(x => x.displayListId)
                .filter(x => x !== null && x !== undefined)
                .map(x => x.toString());
            state.targetId = ids.length ? ids.join(',') : null;

            const names = section.sectionDisplayLists
                .map(x => x.name)
                .filter(x => !!x);
            if (names.length <= 1) {
                state.targetName = names[0] || '';
            } else {
                state.targetName = `${names[0]} +${names.length - 1} more`;
            }
        } else if (section.sectionBroadcast) {
            state.type       = LayoutMainZoneType.Broadcast;
            state.targetId   = section.sectionBroadcast.broadcastId?.toString() ?? null;
            state.targetName = section.sectionBroadcast.name || '';
        } else if (section.sectionScheduler) {
            state.type       = LayoutMainZoneType.Schedule;
            state.targetId   = section.sectionScheduler.schedulerId?.toString() ?? null;
            state.targetName = section.sectionScheduler.name || '';
        } else if (section.sectionMediaApp) {
            state.type       = LayoutMainZoneType.Media;
            state.targetId   = section.sectionMediaApp.userMediaAppId?.toString() ?? null;
            state.targetName = section.sectionMediaApp.name || '';
        } else if (section.widgets?.length) {
            state.type    = LayoutMainZoneType.Widget;
            state.widgetTexts = {};
            // Assign unique frontend keys to multiple CustomText instances; keep other types as-is
            let customTextCount = 0;
            state.widgets = section.widgets.map(w => {
                if (w.type === 'CustomText') {
                    const key = customTextCount === 0 ? 'CustomText' : `CustomText_${customTextCount}`;
                    customTextCount++;
                    if (w.properties) {
                        try {
                            const text = JSON.parse(w.properties)?.customText ?? '';
                            if (text) { state.widgetTexts[key] = text; }
                        } catch { /* ignore */ }
                    }
                    return key;
                }
                return w.type;
            }).filter(t => !!t);
            // Backward compat: mirror first-instance text to legacy customText field
            state.customText = state.widgetTexts['CustomText'] ?? '';
            // Restore logoUrl from Logo widget properties
            const logoWidget = section.widgets.find(w => w.type === 'Logo');
            if (logoWidget?.properties) {
                try { state.logoUrl = JSON.parse(logoWidget.properties)?.logoUrl ?? ''; } catch { /* ignore */ }
            }
            if (!state.logoUrl && section.sectionProperties) {
                try { state.logoUrl = JSON.parse(section.sectionProperties)?.logoUrl ?? ''; } catch { /* ignore */ }
            }
            // Restore weather widget properties
            const weatherWidget = section.widgets.find(w => w.type === 'Weather');
            if (weatherWidget?.properties) { state.weatherProperties = weatherWidget.properties; }
            // Restore text crawl widget properties
            const textCrawlWidget = section.widgets.find(w => w.type === 'TextCrawl');
            if (textCrawlWidget?.properties) { state.textCrawlProperties = textCrawlWidget.properties; }
            // Restore per-widget sizes from widget properties
            let customTextSizeIdx = 0;
            section.widgets.forEach(w => {
                try {
                    const size = JSON.parse(w.properties || '{}')?.size;
                    if (size === 'small' || size === 'large') {
                        let sizeKey = w.type;
                        if (w.type === 'CustomText') {
                            sizeKey = customTextSizeIdx === 0 ? 'CustomText' : `CustomText_${customTextSizeIdx}`;
                            customTextSizeIdx++;
                        }
                        state.widgetSizes[sizeKey] = size;
                    } else if (w.type === 'CustomText') { customTextSizeIdx++; }
                } catch { /* ignore */ }
            });
        } else if (section.sectionProperties) {
            try {
                const props = JSON.parse(section.sectionProperties);
                if (props.customText !== undefined) {
                    // Migrate legacy standalone CustomText zone → Widget + CustomText widget
                    state.type       = LayoutMainZoneType.Widget;
                    state.widgets    = ['CustomText'];
                    state.customText = props.customText;
                    state.widgetTexts = { 'CustomText': props.customText };
                }
            } catch { /* ignore malformed JSON */ }
        }

        // Restore background + weather from sectionProperties (present on all zone types)
        if (section.sectionProperties) {
            try {
                const sp = JSON.parse(section.sectionProperties);
                if (sp.backgroundColor)   { state.backgroundColor   = sp.backgroundColor; }
                if (sp.backgroundImage)    { state.backgroundImage    = sp.backgroundImage; }
                if (sp.weatherProperties)  { state.weatherProperties  = sp.weatherProperties; }
                if (sp.showMediaFill === 'false') { state.showMediaFill = false; }
            } catch { /* ignore */ }
        }

        zoneStates[code] = state;
    });

    return zoneStates;
}

// ---------------------------------------------------------------------------
// Shared media item shape used by LayoutZonePreviewComponent,
// LayoutBuilderComponent, and LayoutPreviewComponent.
// ---------------------------------------------------------------------------

export interface LayoutPreviewMediaItem {
    mediaAppType: number;
    userMediaAppId: number;
    url: string;
    downloadUrl: string;
    lengthInSeconds: number;
    startTime: any;
    endTime: any;
    startDateTime: any;
    endDateTime: any;
    appProperties: string;
    webPageAutoZoom: boolean;
    webPageZoomPercent: number;
    webPageRefreshInterval: number;
    showSubtitle: boolean;
}
